import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";

const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_CONCURRENT = 3;
const CONCURRENT_CHUNKS = 4; // parallel chunk uploads within a single file
const MAX_CHUNK_RETRIES = 6;
const MANIFEST_KEY = "nv_upload_manifest";

const UploadManagerContext = createContext(null);
const stripExt = (name) => name.replace(/\.[^/.]+$/, "");
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Capture a poster frame client-side (deploy-safe, no ffmpeg).
function captureThumbnail(file) {
  return new Promise((resolve) => {
    try {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = URL.createObjectURL(file);
      const done = (blob) => { try { URL.revokeObjectURL(video.src); } catch { /* ignore */ } resolve(blob); };
      video.onloadeddata = () => {
        const d = video.duration && isFinite(video.duration) ? video.duration : 2;
        video.currentTime = Math.min(1, d / 2);
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          if (!canvas.width || !canvas.height) return done(null);
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => done(b), "image/jpeg", 0.8);
        } catch { done(null); }
      };
      video.onerror = () => done(null);
      setTimeout(() => done(null), 6000);
    } catch { resolve(null); }
  });
}

const waitOnline = () =>
  new Promise((resolve) => {
    if (navigator.onLine) return resolve();
    const on = () => { window.removeEventListener("online", on); resolve(); };
    window.addEventListener("online", on);
  });

export function UploadManagerProvider({ children }) {
  const [uploads, setUploads] = useState([]); // {id, name, size, eventId, status, progress, error}
  const [minimized, setMinimized] = useState(false);

  const filesRef = useRef({});        // id -> File
  const metaRef = useRef({});         // id -> {eventId, price, uploadId, canceled, needsFile}
  const controllersRef = useRef({});  // id -> AbortController (current chunk)
  const queueRef = useRef([]);        // pending ids
  const activeRef = useRef(0);
  const listenersRef = useRef({});    // eventId -> Set(cb)
  const manifestRef = useRef({});     // id -> {id, uploadId, name, size, eventId, price, progress}
  const hydratedRef = useRef(false);  // guard so manifest loads only once (StrictMode-safe)

  const writeManifest = useCallback(() => {
    try {
      const entries = Object.values(manifestRef.current);
      if (entries.length === 0) localStorage.removeItem(MANIFEST_KEY);
      else localStorage.setItem(MANIFEST_KEY, JSON.stringify(entries));
    } catch { /* ignore */ }
  }, []);

  const setManifest = useCallback((id, entry) => {
    manifestRef.current[id] = { ...manifestRef.current[id], ...entry };
    writeManifest();
  }, [writeManifest]);

  const removeManifest = useCallback((id) => {
    delete manifestRef.current[id];
    writeManifest();
  }, [writeManifest]);

  // On mount, surface any interrupted uploads from a previous session.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(MANIFEST_KEY) || "[]"); } catch { saved = []; }
    const resumable = (saved || []).filter((e) => e && e.uploadId && e.name);
    if (resumable.length === 0) return;
    const items = resumable.map((e) => {
      manifestRef.current[e.id] = e;
      metaRef.current[e.id] = {
        eventId: e.eventId, price: e.price, uploadId: e.uploadId,
        canceled: false, needsFile: true,
      };
      return { id: e.id, name: e.name, size: e.size, eventId: e.eventId,
               status: "interrupted", progress: e.progress || 0 };
    });
    setUploads((prev) => [...items, ...prev]);
    setMinimized(false);
  }, []);

  const patch = useCallback((id, p) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...p } : u)));
  }, []);

  const notify = useCallback((eventId, video) => {
    const set = listenersRef.current[eventId];
    if (set) set.forEach((cb) => { try { cb(video); } catch { /* ignore */ } });
  }, []);

  const registerEventListener = useCallback((eventId, cb) => {
    if (!listenersRef.current[eventId]) listenersRef.current[eventId] = new Set();
    listenersRef.current[eventId].add(cb);
    return () => { listenersRef.current[eventId]?.delete(cb); };
  }, []);

  const runUpload = useCallback(async (id) => {
    const file = filesRef.current[id];
    const meta = metaRef.current[id];
    if (!file || !meta || meta.canceled) return;

    patch(id, { status: "uploading", error: null });
    const received = new Set();
    let totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    try {
      if (meta.uploadId) {
        // Resuming an existing session — sync received chunks from the server.
        try {
          const { data: st } = await api.get(`/uploads/${meta.uploadId}`);
          totalChunks = st.total_chunks || totalChunks;
          (st.received || []).forEach((i) => received.add(i));
        } catch (e) {
          if (e.response?.status === 404) {
            // Session is gone (expired/cleaned/other env). We still have the file
            // in memory, so quietly start a fresh upload instead of dead-ending.
            meta.uploadId = null;
            received.clear();
            removeManifest(id);
          } else {
            throw e;
          }
        }
      }

      if (!meta.uploadId) {
        // Fresh upload — create a session.
        totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        const { data: init } = await api.post("/uploads/init", {
          event_id: meta.eventId,
          filename: file.name,
          content_type: file.type || "video/mp4",
          price: Number(meta.price || 0),
          total_size: file.size,
          total_chunks: totalChunks,
        });
        meta.uploadId = init.upload_id;
        (init.received || []).forEach((i) => received.add(i));
        patch(id, { uploadId: init.upload_id });
        setManifest(id, {
          id, uploadId: init.upload_id, name: stripExt(file.name),
          size: file.size, eventId: meta.eventId, price: meta.price, progress: 0,
        });

        // Best-effort thumbnail (non-blocking on failure)
        try {
          const thumb = await captureThumbnail(file);
          if (thumb && !meta.canceled) {
            const tf = new FormData();
            tf.append("file", thumb, "thumb.jpg");
            await api.post(`/uploads/${init.upload_id}/thumbnail`, tf);
          }
        } catch { /* ignore */ }
      }

      const loadedMap = {}; // in-flight chunk index -> bytes loaded
      const activeControllers = new Set();
      controllersRef.current[id] = activeControllers;

      const progressFor = () => {
        const inflight = Object.values(loadedMap).reduce((a, b) => a + b, 0);
        const uploaded = Math.min(file.size, received.size * CHUNK_SIZE + inflight);
        return Math.min(99, Math.round((uploaded / file.size) * 100));
      };

      const pending = [];
      for (let i = 0; i < totalChunks; i += 1) if (!received.has(i)) pending.push(i);
      patch(id, { progress: progressFor() });

      const uploadChunk = async (i) => {
        const blob = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
        let attempt = 0;
        for (;;) {
          if (meta.canceled) throw new Error("__canceled__");
          const controller = new AbortController();
          activeControllers.add(controller);
          try {
            await api.put(`/uploads/${meta.uploadId}/chunk/${i}`, blob, {
              headers: { "Content-Type": "application/octet-stream" },
              signal: controller.signal,
              onUploadProgress: (e) => { loadedMap[i] = e.loaded; patch(id, { progress: progressFor() }); },
            });
            activeControllers.delete(controller);
            received.add(i);
            delete loadedMap[i];
            patch(id, { progress: progressFor() });
            setManifest(id, { progress: progressFor() });
            return;
          } catch (err) {
            activeControllers.delete(controller);
            delete loadedMap[i];
            if (meta.canceled) throw new Error("__canceled__");
            attempt += 1;
            if (attempt > MAX_CHUNK_RETRIES) throw err;
            patch(id, { status: "retrying" });
            await waitOnline();
            await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 15000)));
            // Re-sync received chunks from server so we resume, not restart
            try {
              const { data: st } = await api.get(`/uploads/${meta.uploadId}`);
              (st.received || []).forEach((x) => received.add(x));
            } catch { /* ignore */ }
            patch(id, { status: "uploading" });
            if (received.has(i)) return;
          }
        }
      };

      // Bounded worker pool: upload up to CONCURRENT_CHUNKS chunks in parallel.
      let cursor = 0;
      const worker = async () => {
        while (cursor < pending.length) {
          if (meta.canceled) throw new Error("__canceled__");
          const idx = pending[cursor];
          cursor += 1;
          if (received.has(idx)) continue;
          await uploadChunk(idx);
        }
      };
      const poolSize = Math.min(CONCURRENT_CHUNKS, Math.max(1, pending.length));
      await Promise.all(Array.from({ length: poolSize }, () => worker()));

      const { data: video } = await api.post(`/uploads/${meta.uploadId}/complete`);
      if (video && video.processing) {
        // Finalize (reassemble + store) runs server-side; poll until the clip is ready.
        patch(id, { status: "processing", progress: 100 });
        removeManifest(id);
        let polls = 0;
        for (;;) {
          if (meta.canceled) throw new Error("__canceled__");
          if (polls > 400) { patch(id, { status: "error", error: "Processing timed out" }); return; }
          polls += 1;
          await new Promise((r) => setTimeout(r, 3000));
          let st;
          try {
            ({ data: st } = await api.get(`/videos/${video.id}/status`));
          } catch { continue; }
          if (st.error) { patch(id, { status: "error", error: st.error_msg || "Processing failed" }); return; }
          if (st.ready) break;
        }
      } else {
        removeManifest(id);
      }
      patch(id, { status: "done", progress: 100 });
      notify(meta.eventId, video);
    } catch (err) {
      if (meta.canceled || err.message === "__canceled__") {
        // best-effort server cleanup handled in cancel()
      } else {
        patch(id, { status: "error", error: "Upload failed" });
      }
    } finally {
      delete controllersRef.current[id];
    }
  }, [patch, notify, setManifest, removeManifest]);

  const pump = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT && queueRef.current.length > 0) {
      const id = queueRef.current.shift();
      const meta = metaRef.current[id];
      if (!meta || meta.canceled) continue;
      activeRef.current += 1;
      runUpload(id).finally(() => {
        activeRef.current -= 1;
        pump();
      });
    }
  }, [runUpload]);

  const enqueue = useCallback((fileList, { eventId, price }) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("video/"));
    if (files.length === 0) return 0;
    const items = files.map((file) => {
      const id = uid();
      filesRef.current[id] = file;
      metaRef.current[id] = { eventId, price, uploadId: null, canceled: false };
      queueRef.current.push(id);
      return { id, name: stripExt(file.name), size: file.size, eventId, status: "pending", progress: 0 };
    });
    setUploads((prev) => [...items, ...prev]);
    setMinimized(false);
    pump();
    return files.length;
  }, [pump]);

  const cancel = useCallback(async (id) => {
    const meta = metaRef.current[id];
    if (meta) meta.canceled = true;
    const ctrls = controllersRef.current[id];
    if (ctrls && typeof ctrls.forEach === "function") {
      ctrls.forEach((c) => { try { c.abort(); } catch { /* ignore */ } });
    } else if (ctrls?.abort) {
      ctrls.abort();
    }
    queueRef.current = queueRef.current.filter((x) => x !== id);
    if (meta?.uploadId) { try { await api.delete(`/uploads/${meta.uploadId}`); } catch { /* ignore */ } }
    setUploads((prev) => prev.filter((u) => u.id !== id));
    delete filesRef.current[id];
    delete metaRef.current[id];
    removeManifest(id);
  }, [removeManifest]);

  const retry = useCallback((id) => {
    const meta = metaRef.current[id];
    if (!meta) return;
    meta.canceled = false;
    patch(id, { status: "pending", error: null });
    queueRef.current.push(id);
    pump();
  }, [patch, pump]);

  // Resume an interrupted upload after a page reload — user re-selects the file.
  const resume = useCallback((id, file) => {
    const meta = metaRef.current[id];
    const entry = manifestRef.current[id];
    if (!meta || !entry) return { ok: false, error: "Upload not found" };
    if (!file.type.startsWith("video/")) return { ok: false, error: "Please select a video file" };
    if (file.size !== entry.size) {
      return { ok: false, error: "That file doesn't match the interrupted upload (different size)" };
    }
    filesRef.current[id] = file;
    meta.canceled = false;
    meta.needsFile = false;
    patch(id, { status: "pending", error: null });
    queueRef.current.push(id);
    setMinimized(false);
    pump();
    return { ok: true };
  }, [patch, pump]);

  const dismiss = useCallback((id) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
    delete filesRef.current[id];
    delete metaRef.current[id];
    removeManifest(id);
  }, [removeManifest]);

  const clearFinished = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== "done"));
  }, []);

  // Close the widget: dismiss all non-active items (done/error/interrupted) and
  // clear their persisted manifest entries. In-progress uploads are kept running.
  const closeManager = useCallback(() => {
    setUploads((prev) => {
      const active = ["uploading", "pending", "retrying"];
      const removed = prev.filter((u) => !active.includes(u.status));
      removed.forEach((u) => {
        delete filesRef.current[u.id];
        delete metaRef.current[u.id];
        removeManifest(u.id);
      });
      return prev.filter((u) => active.includes(u.status));
    });
  }, [removeManifest]);

  const value = {
    uploads, minimized, setMinimized,
    enqueue, cancel, retry, resume, dismiss, clearFinished, closeManager, registerEventListener,
  };

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
}

export function useUploadManager() {
  return useContext(UploadManagerContext);
}
